import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

// Icon component matching landing page style
const ZapIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const PrivacyPolicy = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        // Scrolling up or near top - show header
        setIsVisible(true);
      } else {
        // Scrolling down - hide header
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Privacy Policy | QuoteTier"
        description="Read QuoteTier's Privacy Policy to learn how we collect, use, and protect your data."
        canonical="/privacy"
      />
      {/* Standardized floating header matching main site */}
      <header 
        className={`fixed top-4 left-1/2 z-50 w-[90%] max-w-4xl -translate-x-1/2 rounded-2xl border border-black/10 bg-white/95 shadow-lg transition-all duration-300 ease-in-out ${
          isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center">
            <ZapIcon className="h-4 w-4 mr-2 text-primary" />
            <span className="font-instrument text-xl md:text-2xl">QuoteTier</span>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
            <Link to="/blog/" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup">
              <Button className="font-instrument text-xl px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Start Free
              </Button>
            </Link>
          </nav>
          
          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center space-x-2">
            <Link to="/blog/" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup">
              <Button className="font-instrument text-sm px-3 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content with spacing for floating header */}
      <main className="py-16 mt-20">
        <div className="container max-w-4xl mx-auto px-4">
          {/* Title Section */}
          <div className="text-center mb-16">
            <h1 className="font-instrument text-4xl sm:text-5xl font-bold tracking-tight mb-4">Privacy Policy</h1>
            <p className="font-jetbrains text-muted-foreground text-lg tracking-tight">Last updated: {currentDate}</p>
          </div>

          {/* Content */}
          <div className="bg-white border-2 border-black rounded-lg p-8 sm:p-12">
            <div className="prose prose-lg max-w-none">
              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Introduction</h2>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  QuoteTier ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web application and services.
                </p>
                <p className="font-jetbrains text-gray-700 leading-relaxed">
                  By using our service, you consent to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, do not use our service.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Information We Collect</h2>
                
                <h3 className="font-instrument text-xl font-medium mb-4">Personal Information</h3>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  We collect information you provide directly to us, including:
                </p>
                <ul className="font-jetbrains space-y-2 mb-8 text-gray-700">
                  <li>• Account information (name, email address, password)</li>
                  <li>• Business information (company name, address, phone number)</li>
                  <li>• Payment information (processed securely through Stripe)</li>
                  <li>• Quote data (client information, pricing, project details)</li>
                  <li>• Profile information (business logo, preferences)</li>
                </ul>

                <h3 className="font-instrument text-xl font-medium mb-4">Automatically Collected Information</h3>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  We automatically collect certain information about your device and usage:
                </p>
                <ul className="font-jetbrains space-y-2 mb-8 text-gray-700">
                  <li>• Device information (IP address, browser type, operating system)</li>
                  <li>• Usage data (pages visited, time spent, features used)</li>
                  <li>• Quote viewing analytics (when quotes are accessed, view counts)</li>
                  <li>• Performance data (loading times, errors)</li>
                </ul>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">How We Use Your Information</h2>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="font-jetbrains space-y-2 mb-8 text-gray-700">
                  <li>• Provide, maintain, and improve our services</li>
                  <li>• Process your transactions and manage your account</li>
                  <li>• Send you quotes, confirmations, and service communications</li>
                  <li>• Provide customer support and respond to your requests</li>
                  <li>• Monitor usage and analyze trends to improve our platform</li>
                  <li>• Detect, prevent, and address technical issues and security threats</li>
                  <li>• Comply with legal obligations and protect our rights</li>
                </ul>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Information Sharing and Disclosure</h2>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:
                </p>

                <h3 className="font-instrument text-xl font-medium mb-4">Service Providers</h3>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  We share information with trusted third-party service providers who assist us in operating our platform:
                </p>
                <ul className="font-jetbrains space-y-2 mb-8 text-gray-700">
                  <li>• Supabase (database and authentication services)</li>
                  <li>• Stripe (payment processing)</li>
                  <li>• Cloudflare (content delivery and security)</li>
                  <li>• Email service providers (transactional emails)</li>
                </ul>

                <h3 className="font-instrument text-xl font-medium mb-4">Legal Requirements</h3>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-8">
                  We may disclose your information if required by law, court order, or government regulation, or if we believe disclosure is necessary to protect our rights, property, or safety, or that of our users or the public.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Data Security</h2>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  We implement appropriate technical and organizational security measures to protect your personal information:
                </p>
                <ul className="font-jetbrains space-y-2 mb-8 text-gray-700">
                  <li>• SSL/TLS encryption for data transmission</li>
                  <li>• Encrypted data storage with Supabase</li>
                  <li>• Regular security audits and updates</li>
                  <li>• Access controls and authentication requirements</li>
                  <li>• Secure payment processing through Stripe</li>
                </ul>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Your Rights and Choices</h2>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  You have the following rights regarding your personal information:
                </p>
                <ul className="font-jetbrains space-y-2 mb-6 text-gray-700">
                  <li>• <strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                  <li>• <strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li>• <strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                  <li>• <strong>Portability:</strong> Request a machine-readable copy of your data</li>
                  <li>• <strong>Objection:</strong> Object to certain processing of your personal information</li>
                </ul>
                <p className="font-jetbrains text-gray-700 leading-relaxed">
                  To exercise these rights, please contact us through our support system. We will respond to your request within 30 days.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Contact Information</h2>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy or our privacy practices, please contact us through our platform's support system or reach out to us directly.
                </p>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  You can also review our <Link to="/terms" className="text-blue-600 hover:text-blue-800 underline">Terms of Service</Link> or return to our <Link to="/" className="text-blue-600 hover:text-blue-800 underline">homepage</Link> to learn more about QuoteTier's interactive quote builder.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      {/* Footer matching landing page */}
      <footer className="border-t-2 border-black">
        <div className="container max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row justify-between items-center">
          <p className="font-jetbrains text-sm text-muted-foreground">&copy; 2025 QuoteTier. All rights reserved. <span className="footer-credit">Site by <a href="https://novanorthstudio.com/?utm_source=quotetier.com&utm_medium=referral&utm_campaign=site-credit">Nova North Studio</a></span></p>
          <div className="flex gap-4 mt-4 md:mt-0">
            <Link to="/terms" className="font-jetbrains text-sm text-muted-foreground hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="font-jetbrains text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
            <span className="font-jetbrains text-sm text-muted-foreground">Contact</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
